import { useState, useEffect } from 'react';
import { ClipboardList, Bell, BellOff, Check } from 'lucide-react';

interface ChecklistItem {
  day: string;
  task: string;
  goal: string;
  metric?: string;
}

interface Props {
  startup: Record<string, any>;
  validationPlanData: Record<string, any> | null;
  onTaskToggle: (taskId: string, completed: boolean) => Promise<void>;
  onSubmitResults?: () => void;
  pipelineCreatedAt?: string;
}

const C = {
  bg: '#0d0d1f',
  surface: '#161627',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  accent: '#00d4ff',
  green: '#22c55e',
  text: '#f0f0ff',
  text2: '#8888aa',
  text3: '#444466',
};

const DAY_LABELS = ['Day 1-2', 'Day 3-4', 'Day 5-7', 'Day 8-10', 'Day 11-14'];

function DailyReminderToggle() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem('sf_daily_reminder') === '1');
  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem('sf_daily_reminder', next ? '1' : '0');
  };
  return (
    <button
      onClick={toggle}
      title={enabled ? 'Daily reminders enabled (click to disable)' : 'Enable daily task reminders'}
      style={{
        fontSize: 10, padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
        background: enabled ? `${C.accent}15` : 'transparent',
        border: `1px solid ${enabled ? `${C.accent}40` : C.border}`,
        color: enabled ? C.accent : C.text3,
      }}
    >
      {enabled ? <><Bell size={12} /> Reminders on</> : <><BellOff size={12} /> Remind me</>}
    </button>
  );
}

function TaskRow({ task, completed, onToggle }: {
  task: ChecklistItem;
  completed: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%',
      padding: '8px 12px', marginBottom: 4, textAlign: 'left',
      background: completed ? `${C.green}06` : C.bg,
      border: `1px solid ${completed ? `${C.green}20` : C.border}`,
      borderRadius: 8, opacity: completed ? 0.7 : 1,
    }}>
      <button
        onClick={onToggle}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, marginTop: 1 }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: 4,
          background: completed ? C.green : 'transparent',
          border: `2px solid ${completed ? C.green : C.text3}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s ease',
        }}>
          {completed && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: 13, color: completed ? C.green : C.text,
          fontWeight: 500, textDecoration: completed ? 'line-through' : 'none',
          transition: 'color 0.3s',
        }}>
          {task.task}
        </p>
        {task.goal && <p style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{task.goal}</p>}
      </div>
    </div>
  );
}

export default function ValidationTracker({ startup, validationPlanData, onTaskToggle, onSubmitResults, pipelineCreatedAt }: Props) {
  const startupId = startup.id as string | undefined;
  const checklist: ChecklistItem[] = (validationPlanData?.checklist as ChecklistItem[]) ?? [];
  const [pulsingGroup, setPulsingGroup] = useState<string | null>(null);

  // localStorage-backed completion state keyed by day
  const [completedTasks, setCompletedTasks] = useState<{ day: string; completed: boolean }[]>(() => {
    if (startupId) {
      try {
        const saved = localStorage.getItem(`validation_tasks_${startupId}`);
        if (saved) return JSON.parse(saved);
      } catch { /* ignore */ }
    }
    return DAY_LABELS.map(day => ({ day, completed: false }));
  });

  // Persist to localStorage on change
  useEffect(() => {
    if (startupId) {
      localStorage.setItem(`validation_tasks_${startupId}`, JSON.stringify(completedTasks));
    }
  }, [completedTasks, startupId]);

  const handleToggle = (day: string) => {
    setCompletedTasks(prev =>
      prev.map(t => t.day === day ? { ...t, completed: !t.completed } : t)
    );
    // Also try to sync to server (best-effort)
    const current = completedTasks.find(t => t.day === day);
    onTaskToggle(day, !(current?.completed ?? false)).catch(() => {});
  };

  // Derive counts
  const totalTasks = checklist.length || DAY_LABELS.length;
  const completedCount = completedTasks.filter(t => t.completed).length;
  const pctDone = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const allComplete = completedCount === totalTasks && totalTasks > 0;

  if (checklist.length === 0 && !validationPlanData) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><ClipboardList size={16} className="text-brand-500" /> Validation Tracker</h3>
        <p style={{ fontSize: 13, color: C.text2 }}>
          Complete your pipeline to generate your validation checklist.
        </p>
      </div>
    );
  }

  // Calculate current validation day
  const validationStartKey = startupId ? `sf_validation_started_${startupId}` : null;
  let currentDay = 1;
  if (validationStartKey) {
    let startDate = startup.validationStartedAt || localStorage.getItem(validationStartKey);
    if (!startDate && pipelineCreatedAt) startDate = pipelineCreatedAt;
    if (!startDate) {
      startDate = new Date().toISOString();
      localStorage.setItem(validationStartKey, startDate);
    } else if (!localStorage.getItem(validationStartKey)) {
      localStorage.setItem(validationStartKey, startDate);
    }
    const diffMs = Date.now() - new Date(startDate).getTime();
    currentDay = Math.min(Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1), 14);
  }
  const pastDeadline = currentDay >= 14;

  // Group checklist items by day label
  const groups = DAY_LABELS.map(label => {
    const items = checklist.filter(item => item.day === label);
    // Fallback: if no exact match, find items containing the day range
    if (items.length === 0) {
      const rangeMatch = checklist.filter(item =>
        item.day?.toLowerCase().replace(/\s/g, '') === label.toLowerCase().replace(/\s/g, '')
      );
      items.push(...rangeMatch);
    }
    return { label, items };
  });

  // If items didn't match any group, distribute sequentially
  const assignedDays = new Set(groups.flatMap(g => g.items.map(i => i.day)));
  const unassigned = checklist.filter(i => !assignedDays.has(i.day));
  if (unassigned.length > 0 && checklist.length > 0) {
    // Put each unassigned item into the matching group or distribute evenly
    unassigned.forEach((item, idx) => {
      const groupIdx = Math.min(idx, groups.length - 1);
      groups[groupIdx].items.push(item);
    });
  }

  return (
    <div className="card" style={{ padding: 20, position: 'relative', overflow: 'hidden' }}>
      {/* CSS confetti on group completion */}
      {pulsingGroup && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none', height: 40, overflow: 'hidden' }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: 6, height: 6, borderRadius: '50%',
              background: [C.primary, C.green, C.accent, '#f97316', '#ff4466'][i % 5],
              left: `${8 + i * 8}%`,
              animation: `confetti-fall 1s ease-out ${i * 0.05}s forwards`,
              opacity: 0,
            }} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}><ClipboardList size={16} className="text-brand-500" /> Validation Tracker</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <DailyReminderToggle />
          <span style={{ fontSize: 12, fontWeight: 700, color: allComplete ? C.green : C.primary }}>
            {completedCount}/{totalTasks} tasks — {pctDone}%
          </span>
        </div>
      </div>

      <p style={{ fontSize: 12, color: pastDeadline ? C.accent : C.text3, marginBottom: 14 }}>
        {pastDeadline ? 'Day 14 of 14 — Review your results' : `Day ${currentDay} of 14`}
      </p>

      {/* Progress bar */}
      <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: 'hidden', marginBottom: 18 }}>
        <div style={{
          height: '100%', width: `${pctDone}%`,
          background: pctDone >= 80 ? C.green : pctDone >= 50 ? '#f97316' : '#ef4444',
          borderRadius: 3, transition: 'width 0.5s ease',
        }} />
      </div>

      {/* All tasks complete banner */}
      {allComplete && (
        <div style={{
          background: 'rgba(34,197,94,0.1)', border: '1px solid #22c55e',
          borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          textAlign: 'center',
        }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.green, marginBottom: 4 }}>
            Experiment complete! Submit your results to unlock the MVP Blueprint.
          </p>
          {onSubmitResults && (
            <button onClick={onSubmitResults} style={{
              background: C.green, border: 'none', borderRadius: 10,
              color: '#030712', fontSize: 13, fontWeight: 700, padding: '8px 20px',
              cursor: 'pointer', marginTop: 8,
            }}>
              → Submit Results & Unlock Blueprint
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(40px) rotate(360deg); opacity: 0; }
        }
      `}</style>

      {/* Day groups */}
      {groups.filter(g => g.items.length > 0).map(group => {
        const isCompleted = completedTasks.find(t => t.day === group.label)?.completed ?? false;
        const isPulsing = pulsingGroup === group.label;

        return (
          <div key={group.label} style={{ marginBottom: 14 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 8, padding: '4px 0',
              animation: isPulsing ? 'pulse 0.5s ease 3' : 'none',
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: isCompleted ? C.green : C.primary }}>
                {group.label} {isCompleted && '✓'}
              </span>
            </div>
            {group.items.map((item, idx) => (
              <TaskRow
                key={`${group.label}-${idx}`}
                task={item}
                completed={completedTasks.find(t => t.day === group.label)?.completed ?? false}
                onToggle={() => {
                  handleToggle(group.label);
                  // Pulse effect
                  const willComplete = !(completedTasks.find(t => t.day === group.label)?.completed ?? false);
                  if (willComplete) {
                    setPulsingGroup(group.label);
                    setTimeout(() => setPulsingGroup(null), 2000);
                  }
                }}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
